import React, { useEffect, useContext, useState, useRef } from "react";
import { AuthContext } from "../store/ContextStore";
import localStorageService from "../services/localStorageService";
import { useNavigate } from "react-router-dom";

export interface SuggestionItem {
  suggestion: string;
  type: "history" | "generic";
}

interface NLPSearchProps {
  filterdata: any;
  searchString: string;
  setSearchString: (val: string) => void;
  handleSearch: () => void;
  resetFilters: () => void;
  resetSorting: () => void;
  suggestions?: SuggestionItem[];
  onSelectSuggestion?: (val: string) => void;
  isPending?: boolean;
}

function NLPSearch(props: NLPSearchProps) {
  const {
    filterdata,
    searchString,
    setSearchString,
    handleSearch,
    resetFilters,
    resetSorting,
    suggestions,
    onSelectSuggestion,
    isPending = false,
  } = props || {};

  const { team } = useContext(AuthContext);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Keyboard Navigation State
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // CHANGE 1: DO NOT auto-highlight. Reset to -1 when suggestions change.
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions, showDropdown]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const activeElement = listRef.current.children[activeIndex] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  // 1. HANDLE SUBMIT (Runs on "Second Enter" or Button Click)
  function handleSubmit(e: React.FormEvent) {
    if (e) e.preventDefault();
    setShowDropdown(false);

    if (isPending) return;

    if (!searchString || searchString.trim() === "") {
      alert("Please search something.");
      return;
    }
    
    const config = { ...filterdata, searchString: searchString };
    localStorageService.addNLPFilter(config);
    localStorageService.addSearchFilter({});
    localStorageService.addSortFields({});
    resetFilters();
    resetSorting();
    handleSearch();
  }

  // 2. HANDLE SELECTION (Autocomplete behavior)
  const handleSelection = (val: string) => {
    if (onSelectSuggestion) {
      onSelectSuggestion(val);
    }
    setSearchString(val);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || !suggestions || suggestions.length === 0) {
      if (e.key === "Enter") handleSubmit(e);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault(); 
      setActiveIndex((prev) => {
        // If nothing selected (-1), start at 0. Otherwise increment.
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); 
      setActiveIndex((prev) => {
        // If nothing selected (-1), wrap to bottom.
        if (prev === -1) return suggestions.length - 1;
        // If at top (0), go back to input (-1).
        if (prev === 0) return -1;
        return prev - 1;
      });
    } else if (e.key === "Enter") {
      // If an item is active, Autocomplete.
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelection(suggestions[activeIndex].suggestion);
      } else {
        // If no highlight (Index -1), Submit.
        handleSubmit(e);
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  const handleInfoClick = () => {
    window.location.href = "/info/index.html";
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [formRef]);

  useEffect(() => {
    if (filterdata["searchString"] && filterdata["searchString"] !== "") {
      setSearchString(filterdata.searchString);
    }
  }, []);

  const getPlaceholder = () => {
    if (filterdata.sport === "MFB") {
      return filterdata.entity === "Player"
        ? "Start by typing [e.g. Players with rushing yards > 25]"
        : "Start by typing [e.g. Teams with rushing yards > 25]";
    } else {
      return filterdata.entity === "Player"
        ? "Start by typing [e.g. Players with field goals made > 25]"
        : "Start by typing [e.g. Teams with field goals made > 25]";
    }
  };

  const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-400 mr-3">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-400 mr-3">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );

  return (
    <>
      <div className="flex justify-between items-center w-full h-full min-h-[60px]">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="relative flex items-center w-full h-full bg-white border border-[#DEE7F4] rounded-xl px-4 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-500"
        >
          <div className="mr-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5 text-gray-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0a7.5 7.5 0 1 0-10.606 0 7.5 7.5 0 0 0 10.606 0z" />
            </svg>
          </div>

          <input
            ref={inputRef}
            type="text"
            placeholder={getPlaceholder()}
            className="flex-grow bg-transparent text-sm font-normal font-primary text-black focus:outline-none text-wrap"
            onFocus={() => {
              if (searchString && searchString.length >= 3) setShowDropdown(true);
            }}
            onChange={(e) => {
              setSearchString(e.target.value);
              setShowDropdown(e.target.value.length >= 3);
            }}
            value={searchString}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />

          <button
            onClick={handleSubmit}
            className="ml-2 text-white px-4 py-2 rounded-lg"
            style={{ backgroundColor: team.colorCode }}
            id="search-btn-123"
            type="submit"
            disabled={isPending}
          >
            {isPending ? "Searching..." : "Search"}
          </button>

          {showDropdown && suggestions && suggestions.length > 0 && (
            <ul
              ref={listRef}
              className="absolute left-0 top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto z-50"
            >
              {suggestions.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <li
                    key={index}
                    className={`px-4 py-3 cursor-pointer text-sm border-b border-gray-100 last:border-b-0 text-left flex items-center transition-colors duration-100 ${
                      isActive ? "bg-gray-100" : "hover:bg-gray-50 bg-white"
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSelection(item.suggestion);
                    }}
                  >
                    {["history", "global"].includes(item.type) ? <ClockIcon /> : <SearchIcon />}
                    <span className={`truncate ${isActive ? "text-gray-900 font-medium" : "text-gray-700"}`}>
                      {item.suggestion}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </form>

        <div className="ml-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleInfoClick}
            height="32px"
            viewBox="0 -960 960 960"
            width="32px"
            fill={team.colorCode}
            stroke={team.colorCode}
            strokeWidth="32"
            className="cursor-pointer hover:opacity-80 transition-opacity"
          >
            <path d="M478-240q21 0 35.5-14.5T528-290q0-21-14.5-35.5T478-340q-21 0-35.5 14.5T428-290q0 21 14.5 35.5T478-240Zm-36-154h74q0-33 7.5-52t42.5-52q26-26 41-49.5t15-56.5q0-56-41-86t-97-30q-57 0-92.5 30T342-618l66 26q5-18 22.5-39t53.5-21q32 0 48 17.5t16 38.5q0 20-12 37.5T506-526q-44 39-54 59t-10 73Zm38 314q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
          </svg>
        </div>
      </div>
    </>
  );
}

export default NLPSearch;
